/**
 * Aba Gestão — centro administrativo PMO do projeto.
 */
import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  BookOpen,
  FileText,
  Shield,
  Users,
  UserCheck,
  History,
} from 'lucide-react';
import { DataGridEnterprise } from '../layout/DataGridEnterprise';
import type { DataGridColumn } from '../layout/DataGridEnterprise';
import { EnterpriseBadge } from '../layout/EnterpriseTable';
import type { ProjectMember, ProjectAttachment } from '../../types/enterpriseProject';
import { MEMBER_ROLE_LABELS, OPERATIONAL_ROLE_LABELS } from '../../types/enterpriseProject';
import type { Invitation } from '../../services/invitationService';
import type { RoadmapItem } from '../../types/consultingProject';
import type { RecentActivityItem } from '../../services/projectService';
import { IssueService } from '../../services/issueService';
import { ISSUE_SEVERITY_LABELS } from '../../types/issue';
import { SectionPanel, fmtDate, ProjectEmptyState } from './projectHubShared';

interface ProjectManagementTabProps {
  orgId: string;
  projectId: string;
  ownerName: string;
  members: ProjectMember[];
  membersLoading: boolean;
  pendingInvites: Invitation[];
  attachments: ProjectAttachment[];
  roadmapItems: RoadmapItem[];
  recentActivity: RecentActivityItem[];
  membersColumns: DataGridColumn<ProjectMember>[];
  documentsSlot: React.ReactNode;
  onAddMember: () => void;
  onInvite: () => void;
  onNavigateProblems: () => void;
}

export const ProjectManagementTab: React.FC<ProjectManagementTabProps> = ({
  orgId,
  projectId,
  ownerName,
  members,
  membersLoading,
  pendingInvites,
  attachments,
  roadmapItems,
  recentActivity,
  membersColumns,
  documentsSlot,
  onAddMember,
  onInvite,
  onNavigateProblems,
}) => {
  const { data: issues = [] } = useQuery({
    queryKey: ['project-mgmt-issues', orgId, projectId],
    queryFn: () => IssueService.list(orgId, { project_id: projectId }),
    enabled: !!orgId && !!projectId,
    staleTime: 60_000,
  });

  const stakeholders = useMemo(() => {
    const list = members.filter(m =>
      m.role === 'owner'
      || m.operational_role === 'observer'
      || m.operational_role === 'responsible',
    );
    if (list.length === 0 && ownerName !== '—') {
      return [{ name: ownerName, role: 'Patrocinador / Responsável' }];
    }
    return list.map(m => ({
      name: m.user_name ?? m.user_id,
      role: m.operational_role
        ? OPERATIONAL_ROLE_LABELS[m.operational_role]
        : MEMBER_ROLE_LABELS[m.role],
    }));
  }, [members, ownerName]);

  const risks = useMemo(
    () => issues
      .filter(i => i.severity === 'critical' || i.severity === 'high')
      .slice(0, 8),
    [issues],
  );

  const changes = useMemo(
    () => roadmapItems
      .filter(i => i.status === 'postponed' || i.status === 'cancelled' || i.status === 'in_progress')
      .slice(0, 8),
    [roadmapItems],
  );

  const meetingNotes = useMemo(
    () => attachments
      .filter(a => /ata|reuni|meeting|decis/i.test(a.name))
      .slice(0, 8),
    [attachments],
  );

  const decisions = useMemo(
    () => recentActivity
      .filter(a => a.type === 'workflow' || a.type === 'problem')
      .slice(0, 6),
    [recentActivity],
  );

  return (
    <div className="grid gap-2 p-2 lg:grid-cols-2">
      <SectionPanel title="Equipe" icon={<Users size={12} className="text-blue-500" />} className="lg:col-span-2">
        <div className="mb-1.5 flex gap-1.5">
          <button type="button" onClick={onAddMember} className="rounded border border-slate-300 px-2 py-0.5 text-[10px] hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800">
            Adicionar membro
          </button>
          <button type="button" onClick={onInvite} className="rounded border border-slate-300 px-2 py-0.5 text-[10px] hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800">
            Convidar
          </button>
        </div>
        <DataGridEnterprise
          columns={membersColumns}
          data={members}
          rowKey={r => r.user_id}
          isLoading={membersLoading}
          emptyTitle="Nenhum membro"
          emptySubtitle="Adicione a equipe de execução do projeto"
          className="max-h-[200px]"
        />
        {pendingInvites.length > 0 && (
          <p className="mt-1 text-[10px] text-amber-600">{pendingInvites.length} convite(s) pendente(s)</p>
        )}
      </SectionPanel>

      <SectionPanel title="Stakeholders" icon={<UserCheck size={12} className="text-violet-500" />}>
        {stakeholders.length === 0 ? (
          <p className="text-[10px] text-slate-500">Defina patrocinador e interessados na equipe.</p>
        ) : (
          <ul className="space-y-1">
            {stakeholders.map((s, i) => (
              <li key={`${s.name}-${i}`} className="flex items-center justify-between text-[10px]">
                <span className="truncate text-slate-700 dark:text-slate-200">{s.name}</span>
                <span className="text-slate-500">{s.role}</span>
              </li>
            ))}
          </ul>
        )}
      </SectionPanel>

      <SectionPanel title="Riscos" icon={<AlertTriangle size={12} className="text-red-500" />}>
        {risks.length === 0 ? (
          <ProjectEmptyState
            icon={<Shield size={20} />}
            title="Sem riscos críticos"
            description="Problemas de alta severidade vinculados ao projeto aparecerão aqui com plano de mitigação."
            actions={
              <button type="button" onClick={onNavigateProblems} className="text-[10px] text-blue-600 hover:underline">
                Abrir Problemas
              </button>
            }
          />
        ) : (
          <ul className="space-y-1">
            {risks.map(issue => (
              <li key={issue.id} className="rounded border border-slate-100 px-2 py-1 text-[10px] dark:border-slate-800">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium text-slate-700 dark:text-slate-200">{issue.title}</span>
                  <EnterpriseBadge className="bg-red-500/10 text-red-600 border border-red-500/20">
                    {ISSUE_SEVERITY_LABELS[issue.severity]}
                  </EnterpriseBadge>
                </div>
                <p className="mt-0.5 text-slate-500">Status: {issue.status} · Mitigação via plano de ação</p>
              </li>
            ))}
          </ul>
        )}
      </SectionPanel>

      <SectionPanel title="Documentos" icon={<BookOpen size={12} className="text-emerald-500" />} className="lg:col-span-2">
        {documentsSlot}
      </SectionPanel>

      <SectionPanel title="Mudanças" icon={<History size={12} className="text-amber-500" />}>
        {changes.length === 0 ? (
          <p className="text-[10px] text-slate-500">Alterações de cronograma e status do roadmap.</p>
        ) : (
          <ul className="space-y-1">
            {changes.map(item => (
              <li key={item.id} className="flex items-center justify-between text-[10px]">
                <span className="truncate text-slate-700 dark:text-slate-200">{item.title}</span>
                <span className="text-slate-500">{item.status}</span>
              </li>
            ))}
          </ul>
        )}
      </SectionPanel>

      <SectionPanel title="Atas e Decisões" icon={<FileText size={12} className="text-slate-500" />}>
        {meetingNotes.length === 0 && decisions.length === 0 ? (
          <p className="text-[10px] text-slate-500">Anexe atas na pasta de documentos ou registre decisões no roadmap.</p>
        ) : (
          <ul className="space-y-1">
            {meetingNotes.map(file => (
              <li key={file.id} className="text-[10px] text-slate-700 dark:text-slate-200">
                {file.name} · {fmtDate(file.created_at)}
              </li>
            ))}
            {decisions.map(d => (
              <li key={d.id} className="text-[10px] text-slate-600 dark:text-slate-300">
                {d.title} · {fmtDate(d.updated_at)}
              </li>
            ))}
          </ul>
        )}
      </SectionPanel>
    </div>
  );
};
